import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useCorrectIngredient,
  useMarkReviewed,
  useReviewRecipes,
} from '../../api/review';
import type { ReviewIngredient, ReviewRecipe } from '../../api/review';
import RecipeThumb from '../../components/RecipeThumb';
import { Pager } from './shared';

const PAGE_SIZE = 50;

/** The card line beside what we read off it, both editable. */
function IngredientRow({ row }: { row: ReviewIngredient }) {
  const correct = useCorrectIngredient();
  const [quantity, setQuantity] = useState(row.total_quantity ?? '');
  const [unit, setUnit] = useState(row.unit);
  const [name, setName] = useState(row.ingredient_name);

  const dirty =
    quantity !== (row.total_quantity ?? '') || unit !== row.unit || name !== row.ingredient_name;

  return (
    <li className={`review-row${row.verified ? ' is-verified' : ''}`}>
      <p className="review-card-line" title="the line printed on the card">
        {row.raw_text}
      </p>

      <div className="review-parsed">
        <input
          className="review-qty"
          value={quantity}
          inputMode="decimal"
          aria-label="quantity as printed on the card"
          onChange={(event) => setQuantity(event.target.value)}
        />
        <input
          className="review-unit"
          value={unit}
          aria-label="unit"
          onChange={(event) => setUnit(event.target.value)}
        />
        <input
          className="review-name"
          value={name}
          aria-label="ingredient"
          onChange={(event) => setName(event.target.value)}
        />
        <button
          type="button"
          className={dirty ? 'primary' : 'ghost'}
          disabled={correct.isPending}
          onClick={() =>
            correct.mutate({
              id: row.id,
              total_quantity: quantity === '' ? null : quantity,
              unit,
              ingredient_name: name,
            })
          }
        >
          {dirty ? 'Save' : row.verified ? 'Checked' : 'Looks right'}
        </button>
      </div>
    </li>
  );
}

function RecipeBlock({ recipe }: { recipe: ReviewRecipe }) {
  const [open, setOpen] = useState(false);
  const markReviewed = useMarkReviewed();

  /* How many lines the card prints, against how many became rows. The sidecar
     is the ingredient list as published, so a shortfall here is a dropped
     line -- findable without reading the card at all. */
  const printed = recipe.raw_ingredients?.length ?? 0;
  const dropped = printed - recipe.ingredients.length;

  return (
    <article className="review-recipe">
      <button
        type="button"
        className="review-heading"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <RecipeThumb id={recipe.id} title={recipe.title} size="sm" />
        <span className="review-heading__text">
          <span>{recipe.title}</span>
          <span className="muted">
            {recipe.servings} servings · {recipe.ingredients.length} ingredients
            {recipe.unverified_count > 0 && ` · ${recipe.unverified_count} unchecked`}
            {recipe.reviewed_at && ' · checked ✓'}
          </span>
        </span>
        {dropped > 0 && (
          <span className="review-flag" title={`The card prints ${printed} lines`}>
            {dropped} missing
          </span>
        )}
      </button>

      {open && (
        /* Card above, rows beneath. The ingredient block is a wide, short
           strip off the bottom half of a landscape card; in a narrow column
           beside the rows it rendered at about 40% and its 8pt type was
           unreadable, which defeated the point of showing it. */
        <div className="review-body">
          <figure className="review-card">
            <img
              src={`/api/recipes/${recipe.id}/ingredients/?w=1400`}
              srcSet={
                `/api/recipes/${recipe.id}/ingredients/?w=900 900w, ` +
                `/api/recipes/${recipe.id}/ingredients/?w=1400 1400w, ` +
                `/api/recipes/${recipe.id}/ingredients/?w=2000 2000w`
              }
              sizes="(min-width: 60rem) 60rem, 100vw"
              alt={`The ingredient list printed on the card for ${recipe.title}`}
              loading="lazy"
            />
            <figcaption>
              <a
                href={`/api/recipes/${recipe.id}/pdf/`}
                target="_blank"
                rel="noreferrer"
              >
                Open the whole card ↗
              </a>
            </figcaption>
          </figure>

          <div className="review-extraction">
            {dropped > 0 && (
              <p className="review-warning">
                The card prints {printed} ingredient lines and {recipe.ingredients.length}{' '}
                were stored. {dropped === 1 ? 'One line was' : `${dropped} lines were`}{' '}
                dropped — check the list above against the rows.
              </p>
            )}
            <ul className="review-rows">
              {recipe.ingredients.map((row) => (
                <IngredientRow key={row.id} row={row} />
              ))}
            </ul>
            <div className="review-actions">
              <button
                type="button"
                className={recipe.reviewed_at ? 'btn btn--small' : 'btn btn--small btn--primary'}
                disabled={markReviewed.isPending}
                onClick={() =>
                  markReviewed.mutate({ id: recipe.id, reviewed: !recipe.reviewed_at })
                }
              >
                {recipe.reviewed_at ? 'Checked — undo' : 'I have checked this card'}
              </button>
              <Link className="btn btn--small" to={`/recipes/${recipe.id}`}>
                Open recipe
              </Link>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

export default function CardReview() {
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState(true);
  const [page, setPage] = useState(1);
  const { data, isLoading } = useReviewRecipes({ search, pending, page });

  const recipes = data?.results ?? [];
  const total = data?.count ?? 0;

  /* Changing a filter has to reset the page, or narrowing the search from
     page 14 lands on an empty result that looks like "nothing matches". */
  function refilter(apply: () => void) {
    apply();
    setPage(1);
  }

  return (
    <section className="review">
      <h1>Did we read the card right?</h1>
      <p className="muted">
        Correct mistakes in recipe card extraction.
      </p>

      <div className="review-controls">
        <input
          type="search"
          value={search}
          placeholder="Search by recipe or ingredient"
          aria-label="Search recipes to review"
          onChange={(event) => refilter(() => setSearch(event.target.value))}
        />
        <label>
          <input
            type="checkbox"
            checked={pending}
            onChange={(event) => refilter(() => setPending(event.target.checked))}
          />
          Unchecked only
        </label>
      </div>

      {isLoading && <p className="center-note">Loading…</p>}
      {!isLoading && recipes.length === 0 && (
        <p className="muted">Nothing matches those filters.</p>
      )}

      {recipes.map((recipe) => (
        <RecipeBlock key={recipe.id} recipe={recipe} />
      ))}

      <Pager
        page={page}
        total={total}
        pageSize={PAGE_SIZE}
        hasNext={Boolean(data?.next)}
        onPage={setPage}
        noun="recipes"
      />
    </section>
  );
}
